import mitt from 'mitt';
import { DropzoneFile } from '@lendica/components';
import { ProcessBillReponse } from '../payLaterAPI';

export type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface BillProcessingState {
    fileKey: string;
    fileName: string;
    status: ProcessingStatus;
    billId?: string;
    error?: string;
}

type BillEvents = {
    'processing-started': { fileKey: string };
    'processing-complete': { fileKey: string; billId: string };
    'processing-error': { fileKey: string; error: string };
    'state-update': void;
};

class BillUploadManagerClass {
    private emitter = mitt<BillEvents>();
    private processingState = new Map<string, BillProcessingState>();
    private processingQueue: string[] = [];
    private activeProcessing = 0;
    private readonly MAX_CONCURRENT = 3;
    private processBill?: (objectKey: string) => Promise<ProcessBillReponse>;

    constructor() {
        console.log('[BillUploadManager] Initialized');
    }

    setProcessBill(processor: (objectKey: string) => Promise<ProcessBillReponse>): void {
        this.processBill = processor;
    }

    /**
     * Start processing a file
     */
    async startProcessing(file: DropzoneFile): Promise<void> {
        if (!file.key) {
            console.error('[BillUploadManager] Cannot process file without key');
            return;
        }

        // Check if already processing
        if (this.processingState.has(file.key)) {
            console.log('[BillUploadManager] File already being processed:', file.key);
            return;
        }

        // Initialize state
        this.processingState.set(file.key, {
            fileKey: file.key,
            fileName: file.path || file.name || 'unknown',
            status: 'pending',
        });

        // Add to queue
        this.processingQueue.push(file.key);
        this.emitter.emit('state-update');

        // Process next in queue
        this.processNextInQueue();
    }

    private async processNextInQueue(): Promise<void> {
        if (this.activeProcessing >= this.MAX_CONCURRENT || this.processingQueue.length === 0) {
            return;
        }

        const fileKey = this.processingQueue.shift()!;
        const state = this.processingState.get(fileKey);
        if (!state) return;

        // Update status
        state.status = 'processing';
        this.activeProcessing++;
        this.emitter.emit('processing-started', { fileKey });
        this.emitter.emit('state-update');

        try {
            console.log('[BillUploadManager] Processing bill:', fileKey);
            if (!this.processBill) {
                throw new Error('No processBill function configured');
            }
            const result = await this.processBill(fileKey);

            // Update state
            state.status = 'completed';
            state.billId = result.bill.id;

            this.emitter.emit('processing-complete', { fileKey, billId: result.bill.id });
            console.log('[BillUploadManager] Processing complete:', fileKey, result.bill.id);
        } catch (error: any) {
            console.error('[BillUploadManager] Processing failed:', fileKey, error);

            // Update state
            state.status = 'error';
            state.error = error.message || 'Processing failed';

            this.emitter.emit('processing-error', {
                fileKey,
                error: state.error || 'Processing failed',
            });
        } finally {
            this.activeProcessing--;
            this.emitter.emit('state-update');

            // Process next in queue
            this.processNextInQueue();
        }
    }

    /**
     * Get processing state for a file
     */
    getState(fileKey: string): BillProcessingState | undefined {
        return this.processingState.get(fileKey);
    }

    /**
     * Get all processing states
     */
    getAllStates(): Map<string, BillProcessingState> {
        return new Map(this.processingState);
    }

    /**
     * Clear completed or errored states
     */
    clearCompleted(): void {
        const toRemove: string[] = [];
        this.processingState.forEach((state, key) => {
            if (state.status === 'completed' || state.status === 'error') {
                toRemove.push(key);
            }
        });
        toRemove.forEach(key => this.processingState.delete(key));
        this.emitter.emit('state-update');
    }

    /**
     * Subscribe to events
     */
    on<K extends keyof BillEvents>(event: K, handler: (payload: BillEvents[K]) => void): void {
        this.emitter.on(event, handler);
    }

    /**
     * Unsubscribe from events
     */
    off<K extends keyof BillEvents>(event: K, handler: (payload: BillEvents[K]) => void): void {
        this.emitter.off(event, handler);
    }
}

// Singleton instance
export const BillUploadManager = new BillUploadManagerClass();
